import { useEffect, useMemo, useRef } from 'react'
import { UseFormGetValues, UseFormSetValue, UseFormWatch } from 'react-hook-form'

import { useExecuteCommandMutation } from '@redux/api'

import { executeCommandAction, processFieldChangeVariable, useOpenForm } from '@hooks'
import { executeCode, flattenFieldValues, getValuesFromVariables } from '@helpers'
import { ACTION_CODE, ENTITY, EVENT_CODE, FORM_TYPE } from '@constants'
import { ActionRow, GETAllVariables, GETVariableDTO, ObjectDataRecord, Row } from '@types'

import { EVENT_NAME, subscribe, unsubscribe } from '../../../events'

type Params = {
  formActions: ActionRow[] | undefined
  setValue: UseFormSetValue<Row>
  isViewType: boolean
  instanceId: string
  row?: Row
  formVariables?: GETVariableDTO[]
  outerVariables?: GETAllVariables
  watch?: UseFormWatch<Row>
  dialogId?: string
  getValues: UseFormGetValues<Row>
}

type RuntimeData = {
  row: Row | undefined
  formVariables: GETVariableDTO[]
  outerVariables: GETAllVariables | undefined
  watch: UseFormWatch<Row> | undefined
  setValue: UseFormSetValue<Row>
  setFieldActions: ActionRow[]
}

const buildScriptContext = (
  row: Row,
  variableValue: unknown,
  fieldsArray: Row,
  formVariables: GETVariableDTO[],
  outerVariables: GETAllVariables | undefined,
  commandResponse?: ObjectDataRecord
) => {
  const resolvedOuterVariables = getValuesFromVariables(outerVariables)

  return {
    srcObj: { ...row, variableValue, outerVariables: resolvedOuterVariables },
    variableValue,
    rowData: row,
    currentRowValues: flattenFieldValues(fieldsArray),
    formValues: fieldsArray,
    formVariables,
    outerVariables: resolvedOuterVariables,
    enrichedObject: row,
    commandResponse,
  }
}

// useLatestFormVariables обновляет переменные через setState, то есть асинхронно.
// К моменту выполнения скрипта в ref ещё лежит старый массив — поэтому
// обновляем его вручную прямо здесь, не дожидаясь ререндера.
const withUpdatedVariable = (
  formVariables: GETVariableDTO[],
  eventDetail: CustomEvent['detail']
): GETVariableDTO[] => {
  const updated = [...formVariables]
  const index = updated.findIndex(v => v.id === eventDetail.id)

  if (index !== -1) {
    updated[index] = { ...updated[index], value: eventDetail.value }
  } else {
    updated.push({ ...eventDetail })
  }

  return updated
}

const computeProcessedValue = (
  action: ActionRow,
  variableValue: unknown,
  runtime: RuntimeData,
  formVariables: GETVariableDTO[],
  commandResponse?: ObjectDataRecord
): unknown => {
  const jsValue = action.jsValue

  if (!jsValue?.trim()) {
    return variableValue ?? ''
  }

  const fieldsArray = runtime.watch ? runtime.watch() : ({} as Row)
  const row = runtime.row ?? fieldsArray
  const context = buildScriptContext(
    row,
    variableValue,
    fieldsArray,
    formVariables,
    runtime.outerVariables,
    commandResponse
  )
  const result = executeCode(jsValue, context)

  // скрипт не вернул значение — ни одно условие не выполнилось, поле не трогаем
  if (result === undefined) return undefined

  // TODO Возможны проблемы с boolean значениями
  return result === false ? variableValue ?? '' : result
}

const getProcessedActions = (formActions: ActionRow[] | undefined): ActionRow[] =>
  formActions?.filter(
    action =>
      [
        ACTION_CODE.SET_FIELD_VALUE,
        ACTION_CODE.OPEN_ANOTHER_FORM_MODEL,
        ACTION_CODE.OPEN_FORM_MODEL,
      ].includes(action.actionCode as ACTION_CODE) &&
      action.eventCode === EVENT_CODE.ON_VARIABLE_CHANGE
  ) ?? []

export const useSetFormValueOnVariableChange = (params: Params) => {
  const {
    formActions,
    setValue,
    isViewType,
    instanceId,
    row,
    formVariables,
    outerVariables,
    watch,
    dialogId,
    getValues
  } = params

  const [executeCommand] = useExecuteCommandMutation()
  const { openForm } = useOpenForm()

  const setFieldActions = useMemo(() => getProcessedActions(formActions), [formActions])

  const runtimeRef = useRef<RuntimeData>({
    row,
    formVariables: formVariables ?? [],
    outerVariables,
    watch,
    setValue,
    setFieldActions,
  })

  runtimeRef.current = {
    row,
    formVariables: formVariables ?? [],
    outerVariables,
    watch,
    setValue,
    setFieldActions,
  }

  useEffect(() => {
    if (isViewType || setFieldActions.length === 0) return

    const handleVariableChange = (event: CustomEvent) => {
      const eventDetail = event.detail
      if (!eventDetail?.id) return

      const runtime = runtimeRef.current
      const updatedFormVariables = withUpdatedVariable(runtime.formVariables, eventDetail)

      const handleAction = (action: ActionRow, commandResponse?: ObjectDataRecord) => {
        if (action.actionCode === ACTION_CODE.SET_FIELD_VALUE) {
          const processedValue = computeProcessedValue(
            action,
            eventDetail.value,
            runtime,
            updatedFormVariables,
            commandResponse
          )

          // скрипт не вернул значение — ни одно условие не выполнилось, поле не трогаем
          if (processedValue === undefined) return

          runtime.setValue(action.objectFieldCode, processedValue, {
            shouldValidate: true,
            shouldDirty: true,
          })

          // setValue не генерирует type:'change' в watch, поэтому
          // useChangeVariableWithAction не узнает об изменении — вызываем вручную
          const updatedFormValues = getValues()
          processFieldChangeVariable(updatedFormValues, action.objectFieldCode, {
            row: row ?? updatedFormValues,
            formVariables: updatedFormVariables,
            outerVariables,
            actions: formActions ?? [],
            uniqueEventId: instanceId,
            executeCommand,
            openForm,
          })

          return
        }

        openForm(action, {
          formProps: {
            formCode: action.objectFormCode || '',
            event: FORM_TYPE.CREATE,
            viewCode: '',
            title: '',
            id: '',
            objectCode: (action.objectCode as string) ?? '',
            path: '',
            entityType: ENTITY.VIEW,
            isIndependentForm: true,
            variables: updatedFormVariables,
            outerData: {
              srcObj: row,
              commands: [],
            },
          },
          parentDialogId: dialogId ?? null,
          additionalProps: {
            addHistory: true,
          },
        })
      }

      runtime.setFieldActions.forEach(action => {
        if (action.variable?.id !== eventDetail.id) return

        if (action.command) {
          const fieldsArray = runtime.watch ? runtime.watch() : ({} as Row)

          executeCommandAction({
            action,
            context: buildScriptContext(
              row,
              eventDetail.value,
              fieldsArray,
              formVariables,
              runtime.outerVariables
            ),
            executeCommand,
          }).then(commandResponse => handleAction(action, commandResponse))

          return
        }

        handleAction(action)
      })
    }

    subscribe(EVENT_NAME.CHANGE_VARIABLE_VALUE, handleVariableChange, instanceId)

    return () => {
      unsubscribe(EVENT_NAME.CHANGE_VARIABLE_VALUE, handleVariableChange, instanceId)
    }
  }, [isViewType, instanceId, setFieldActions])
}
